import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  type GalleryImageApi,
  adminFetchGallery,
  adminSaveGallery,
  adminDeleteGallery,
  adminUploadGalleryImage,
} from '@/api/client';
import { galleryImages as mockGalleryImages } from '@/mocks/gallery';

function mockToGalleryApi(m: typeof mockGalleryImages[number], index: number): GalleryImageApi {
  return {
    id: -(index + 1),
    image_url: m.src,
    title: m.alt,
    alt_text: m.alt,
    category: 'House Cleaning',
    sort_order: index + 1,
    active: 1,
  };
}

function normalizeActive(val: number | boolean): boolean {
  return val === 1 || val === true;
}

const getCategoryOptions = (t: (k: string) => string) => [
  t('admin_category_house_cleaning'),
  t('admin_category_apartment_cleaning'),
  t('admin_category_office_cleaning'),
  t('admin_category_post_construction'),
  t('admin_category_move_in_out'),
];

export default function AdminGalleryManager() {
  const { t } = useTranslation('home');
  const categoryOptions = getCategoryOptions(t);
  const [images, setImages] = useState<GalleryImageApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<GalleryImageApi>({
    id: 0,
    image_url: '',
    title: '',
    alt_text: '',
    category: categoryOptions[0],
    sort_order: 1,
    active: 1,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadGallery = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setUsingMock(false);
    try {
      const { data, error } = await adminFetchGallery();
      if (error) {
        // Backend unreachable — show mocks as visual reference with warning
        setImages(mockGalleryImages.map(mockToGalleryApi));
        setFetchError(error);
        setUsingMock(true);
      } else if (data.length === 0) {
        // Backend reachable but empty — preload defaults so admin can edit/save
        setImages(mockGalleryImages.map(mockToGalleryApi));
        setUsingMock(true);
      } else {
        setImages(data);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setImages(mockGalleryImages.map(mockToGalleryApi));
      setFetchError(msg);
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const seedDefaultGallery = useCallback(async () => {
    setSeeding(true);
    const categories = ['House Cleaning', 'Office Cleaning', 'House Cleaning', 'House Cleaning', 'Office Cleaning', 'Apartment Cleaning'];
    for (let i = 0; i < mockGalleryImages.length; i++) {
      const m = mockGalleryImages[i];
      await adminSaveGallery({
        image_url: m.src,
        title: m.alt,
        alt_text: m.alt,
        category: categories[i] || 'House Cleaning',
        sort_order: i + 1,
        active: 1,
      });
    }
    setSeeding(false);
    await loadGallery();
  }, [loadGallery]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const handleEdit = (index: number) => {
    setEditing(index);
    setEditForm({ ...images[index] });
  };

  const handleAdd = () => {
    setEditing(-1);
    setEditForm({
      id: 0,
      image_url: '',
      title: '',
      alt_text: '',
      category: categoryOptions[0],
      sort_order: images.length + 1,
      active: 1,
    });
  };

  const saveEdit = async () => {
    // Validate: image_url is required by the backend
    if (!editForm.image_url || editForm.image_url.trim() === '') {
      Swal.fire({
        title: 'Image required',
        text: 'Please upload or select an image before saving.',
        icon: 'warning',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
      return;
    }

    // If editing a mock item (negative id), strip the id so the backend creates a new record
    const payload = {
      ...editForm,
      id: editForm.id > 0 ? editForm.id : undefined,
      sort_order: Number(editForm.sort_order) || 1,
      active: editForm.active === true || editForm.active === 1 ? 1 : 0,
    };
    setLoading(true);
    const res = await adminSaveGallery(payload);
    setLoading(false);
    if (res.success && res.data) {
      Swal.fire({
        title: t('admin_api_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
        timer: 2000,
        timerProgressBar: true,
      });
      await loadGallery();
      setEditing(null);
    } else {
      Swal.fire({
        title: t('admin_api_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
  };

  const handleDelete = (index: number) => {
    const img = images[index];
    Swal.fire({
      title: `${t('admin_delete')} ${t('admin_image')}?`,
      text: t('admin_delete_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_delete'),
      cancelButtonText: t('admin_cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const res = await adminDeleteGallery(img.id);
        setLoading(false);
        if (res.success) {
          Swal.fire({
            title: t('admin_api_deleted'),
            icon: 'success',
            confirmButtonColor: '#3DBFA0',
            timer: 1500,
          });
          await loadGallery();
        } else {
          Swal.fire({
            title: t('admin_api_error'),
            text: res.message || '',
            icon: 'error',
            confirmButtonColor: '#3DBFA0',
            confirmButtonText: t('admin_ok'),
          });
        }
      }
    });
  };

  const handleUploadImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setLoading(true);
    const res = await adminUploadGalleryImage(formData);
    setLoading(false);
    const uploadedUrl = res.data?.url || (res.data as Record<string, string> | undefined)?.path;
    if (res.success && uploadedUrl) {
      Swal.fire({
        title: t('admin_upload_success'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
        timer: 1500,
      });
      setEditForm((prev) => ({ ...prev, image_url: uploadedUrl }));
    } else {
      Swal.fire({
        title: t('admin_upload_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelected}
      />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">
            {t('admin_gallery_manager')}
          </h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_manage_gallery')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-sm"
        >
          <i className="ri-add-line" />
          {t('admin_add_image')}
        </button>
      </div>

      {/* Default gallery banner */}
      {usingMock && !loading && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
            <i className="ri-information-line text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {fetchError ? 'Backend not reachable' : 'Default gallery images loaded'}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {fetchError
                ? `Showing default gallery from the landing page. (${fetchError})`
                : 'These images are displayed on the public site but are not saved in the backend yet. Edit and save them here to enable full management.'}
            </p>
          </div>
          <button
            onClick={loadGallery}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      )}

      {loading && images.length === 0 ? (
        <div className="py-12 text-center text-brand-muted text-sm">
          <i className="ri-loader-4-line animate-spin text-xl mr-2" />
          {t('admin_loading_data')}
        </div>
      ) : images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, index) => (
            <div
              key={img.id}
              className={`group rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-md ${normalizeActive(img.active) ? 'bg-white border-gray-100 hover:border-brand-mint/20' : 'bg-gray-50/50 border-gray-200'}`}
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={img.image_url}
                  alt={img.alt_text || img.title || ''}
                  className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${normalizeActive(img.active) ? '' : 'grayscale opacity-60'}`}
                />
                {!normalizeActive(img.active) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="px-3 py-1 bg-black/60 text-white text-[10px] font-bold rounded-full uppercase">
                      {t('admin_inactive')}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/40 transition-colors duration-300 flex items-center justify-center">
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button
                      onClick={() => handleEdit(index)}
                      className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-brand-mint transition-colors cursor-pointer shadow-lg"
                      aria-label={t('admin_edit')}
                    >
                      <i className="ri-edit-line text-brand-dark text-lg" />
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors cursor-pointer shadow-lg"
                      aria-label={t('admin_delete')}
                    >
                      <i className="ri-delete-bin-line text-brand-dark text-lg" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <p className="text-xs font-medium text-brand-dark truncate">{img.title}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-brand-muted">{img.category}</span>
                  <span className="text-[10px] text-brand-muted">#{img.sort_order}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {images.length === 0 && !loading && !usingMock && (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <div className="w-16 h-16 rounded-full bg-brand-light flex items-center justify-center mx-auto mb-4">
            <i className="ri-gallery-line text-2xl text-brand-muted" />
          </div>
          <h3 className="text-base font-semibold text-brand-dark mb-1">{t('admin_no_images')}</h3>
          <p className="text-sm text-brand-muted mt-1 mb-6">Load the 6 default gallery images from the landing page, or upload a new one manually.</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={seedDefaultGallery}
              disabled={seeding}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-dark text-white text-sm font-semibold rounded-lg hover:bg-brand-dark/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {seeding ? (
                <><i className="ri-loader-4-line animate-spin" />Loading images...</>
              ) : (
                <><i className="ri-download-cloud-line" />Load Default Gallery (6)</>
              )}
            </button>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Add Image Manually
            </button>
          </div>
        </div>
      )}

      {/* Edit/Add Modal */}
      {editing !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-brand-dark/40 backdrop-blur-sm"
            onClick={() => setEditing(null)}
          />
          <div className="relative bg-white rounded-2xl border border-gray-100 shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-brand-dark font-heading">
                  {editing === -1 ? t('admin_add') : t('admin_edit')} {t('admin_image')}
                </h3>
                <p className="text-xs text-brand-muted">{t('admin_manage_gallery')}</p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-brand-dark" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${normalizeActive(editForm.active) ? 'bg-green-100' : 'bg-gray-200'}`}
                  >
                    <i
                      className={`text-lg ${normalizeActive(editForm.active) ? 'ri-eye-line text-green-600' : 'ri-eye-off-line text-gray-500'}`}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-brand-dark">
                      {normalizeActive(editForm.active) ? t('admin_active') : t('admin_inactive')}
                    </p>
                    <p className="text-xs text-brand-muted">
                      {normalizeActive(editForm.active) ? t('admin_visible') : t('admin_hidden')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setEditForm({
                      ...editForm,
                      active: normalizeActive(editForm.active) ? 0 : 1,
                    })
                  }
                  className={`relative w-12 h-7 rounded-full transition-colors duration-200 cursor-pointer ${normalizeActive(editForm.active) ? 'bg-brand-mint' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform duration-200 ${normalizeActive(editForm.active) ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-dark mb-2">
                  {t('admin_preview_image')}
                </label>
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  {editForm.image_url ? (
                    <>
                      <img
                        src={editForm.image_url}
                        alt={editForm.alt_text || editForm.title || ''}
                        className="w-full h-44 object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-brand-dark/0 hover:bg-brand-dark/30 transition-colors">
                        <button
                          onClick={handleUploadImage}
                          className="opacity-0 hover:opacity-100 inline-flex items-center gap-2 px-4 py-2 bg-white text-brand-dark text-sm font-medium rounded-lg shadow-lg transition-opacity cursor-pointer"
                        >
                          <i className="ri-upload-cloud-2-line" />
                          {t('admin_change_image')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-44 bg-gray-50 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-3 rounded-xl">
                      <i className="ri-image-add-line text-gray-400 text-4xl" />
                      <button
                        onClick={handleUploadImage}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer shadow-sm"
                      >
                        <i className="ri-upload-cloud-2-line" />
                        Upload Image
                      </button>
                      <p className="text-xs text-brand-muted">Required — JPG, PNG, WebP</p>
                    </div>
                  )}
                </div>
                {editForm.image_url && (
                  <button
                    onClick={handleUploadImage}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-mint transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line" />
                    Change image
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_image_title')}
                  </label>
                  <input
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder={t('admin_image_example')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_slide_order')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.sort_order}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sort_order: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                  {t('admin_category')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {categoryOptions.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setEditForm({ ...editForm, category: cat })}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${editForm.category === cat ? 'border-brand-mint bg-brand-mint/10 text-brand-mint' : 'border-gray-200 text-brand-muted hover:border-brand-mint/30 hover:text-brand-dark'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                  {t('admin_alt_text')}
                </label>
                <input
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                  value={editForm.alt_text}
                  onChange={(e) => setEditForm({ ...editForm, alt_text: e.target.value })}
                  placeholder={t('admin_alt_placeholder')}
                />
                <p className="text-xs text-brand-muted mt-1">{t('admin_alt_hint')}</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
              <span className="text-xs text-brand-muted">{t('admin_save_to_backend')}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 bg-gray-100 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {t('admin_cancel')}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer shadow-sm disabled:opacity-60"
                >
                  {loading ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-save-line" />
                  )}
                  {t('admin_save_image')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}